import {
  Center,
  ConditionalRendering,
  Loader,
  Modal,
  Text
} from "@marketplace/ui";
import { useAtomValue } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useTranslation } from "react-i18next";
import { MdCheckCircle } from "react-icons/md";

import { formatWithOffset } from "@/utils";

import { automationDetailModalAtom } from "../../atoms/automation-detail-modal-atom";
import { useGetAutomationSummaryQuery } from "../../hooks/queries/use-get-automation-summary-query";

export const AutomationDetailModal = () => {
  const { visible, automationId } = useAtomValue(automationDetailModalAtom);
  const resetModal = useResetAtom(automationDetailModalAtom);
  const { t } = useTranslation("product", { keyPrefix: "automation" });

  const { data, isLoading, isError } = useGetAutomationSummaryQuery({
    queryParams: { id: automationId! },
    options: {
      enabled: visible && !!automationId,
      throwOnError: false,
      retry: false
    }
  });

  const detail = data?.data;

  return (
    <Modal
      opened={visible}
      onClose={resetModal}
      title={
        <Text type="heading" weight="semibold">
          {t("listPage.detailModal.title")}
        </Text>
      }
      size={610}
      withCloseButton
    >
      <ConditionalRendering condition={isLoading}>
        <Center className="my-10">
          <Loader />
        </Center>
      </ConditionalRendering>

      <ConditionalRendering condition={isError}>
        <Center className="my-10">
          <Text className="text-fjord-grey-400">
            {t("listPage.detailModal.notFound")}
          </Text>
        </Center>
      </ConditionalRendering>

      <ConditionalRendering condition={!isLoading && !isError && !!detail}>
        {detail && (
          <div className="space-y-6">
            <div className="ml-4 space-y-4">
              <div className="flex gap-8">
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.detailModal.sellerName")}
                  </Text>
                  <Text className="text-fjord-grey-500 font-semibold">
                    {detail.seller_name ?? "-"}
                  </Text>
                </div>
              </div>

              <div className="flex gap-8">
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.table.columns.productId")}
                  </Text>
                  <Text className="text-fjord-grey-500">
                    {detail.product_id}
                  </Text>
                </div>
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.table.columns.productName")}
                  </Text>
                  <Text className="text-fjord-grey-500 font-semibold">
                    {detail.product_name ?? "-"}
                  </Text>
                </div>
              </div>

              <div className="flex gap-8">
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.table.columns.host")}
                  </Text>
                  <Text className="text-fjord-grey-500 font-semibold">
                    {detail.host ?? "-"}
                  </Text>
                </div>
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.table.columns.hostAuth")}
                  </Text>
                  <Text className="text-fjord-grey-500 font-semibold">
                    {detail.host_auth || "-"}
                  </Text>
                </div>
              </div>
              <div className="flex gap-8">
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.detailModal.createdAt")}
                  </Text>
                  <Text className="text-fjord-grey-500 font-semibold">
                    {formatWithOffset(
                      detail.created_at ?? "",
                      "dd MMM yyyy, HH:mm"
                    )}
                  </Text>
                </div>
                <div className="flex-1 space-y-1">
                  <Text className="text-fjord-grey-300">
                    {t("listPage.detailModal.lastModified")}
                  </Text>
                  <Text className="text-fjord-grey-500 font-semibold">
                    {formatWithOffset(
                      detail.updated_at ?? "",
                      "dd MMM yyyy, HH:mm"
                    )}
                  </Text>
                </div>
              </div>

              <div className="space-y-1">
                <div className="rounded-lg bg-mischka-100 border border-solid border-mischka-400 p-3 space-y-[14px]">
                  {detail.steps.length > 0 ? (
                    detail.steps.map((step, idx) => (
                      <div
                        key={idx}
                        className="space-y-4 border-x-0 border-t-0 pb-[14px] [&:not(:last-child)]:border-b [&:not(:last-child)]:border-solid [&:not(:last-child)]:border-mischka-400"
                      >
                        <div className="grid grid-cols-[auto_1fr] items-center gap-2">
                          <MdCheckCircle
                            className="text-noble-blue-500 shrink-0"
                            size={16}
                          />
                          <Text className="text-fjord-grey-500">
                            {`Step ${idx + 1}`}
                          </Text>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pl-6">
                          <div className="space-y-1 ">
                            <Text className="text-fjord-grey-300">
                              {t("listPage.detailModal.stepType")}
                            </Text>
                            <Text className="text-fjord-grey-500 font-semibold">
                              {step.step_type}
                            </Text>
                          </div>
                          <div className="space-y-1">
                            <Text className="text-fjord-grey-300">
                              {t("listPage.detailModal.stepName")}
                            </Text>
                            <Text className="text-fjord-grey-500 font-semibold">
                              {step.step_name}
                            </Text>
                          </div>
                        </div>
                        <div className="space-y-1 pl-6">
                          <Text className="text-fjord-grey-300">
                            {t("listPage.detailModal.contentType")}
                          </Text>
                          <Text className="text-fjord-grey-500 font-semibold">
                            {step.content_type}
                          </Text>
                        </div>
                      </div>
                    ))
                  ) : (
                    <Text className="text-fjord-grey-400 text-center py-2">
                      {t("listPage.detailModal.noSteps")}
                    </Text>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </ConditionalRendering>
    </Modal>
  );
};
